// src/utils/seo/analytics.js
// Analytics tracking utility for SEO and conversion monitoring

/**
 * Track CTA clicks and conversions
 */
function coarsePath(pathname) {
  const path = String(pathname || '/').split('?')[0];
  if (path.startsWith('/app')) return '/app';
  if (path.startsWith('/cancer')) return '/cancer';
  if (path.startsWith('/rare')) return '/rare-cancers';
  if (path.startsWith('/find-')) return '/directory';
  if (path.startsWith('/second-opinion')) return '/second-opinion';
  return path.slice(0, 80);
}

export const trackCTA = (ctaType, location) => {
  const eventData = {
    event: `cta_click_${ctaType}`,
    location: location,
    page: coarsePath(window.location.pathname),
  };

  // Google Analytics 4. No health text, query strings, or extra parameters.
  if (window.gtag) {
    window.gtag('event', `cta_click_${ctaType}`, {
      event_category: 'CTA',
      event_label: location,
      page_path: coarsePath(window.location.pathname),
    });
  }

  // Custom analytics endpoint (if available)
  if (process.env.REACT_APP_ANALYTICS_ENDPOINT) {
    fetch(process.env.REACT_APP_ANALYTICS_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(eventData)
    }).catch(err => console.error('Analytics error:', err));
  }

  // Console log for development
  if (process.env.NODE_ENV === 'development') {
    console.log('CTA Tracked:', eventData);
  }
};

/**
 * Track page views for SEO monitoring
 */
export const trackPageView = (pagePath) => {
  if (window.gtag) {
    window.gtag('config', process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-XXXXXXXXXX', {
      page_path: coarsePath(pagePath),
    });
  }
};

/**
 * Track keyword searches (internal search)
 */
export const trackKeywordSearch = () => {
  if (window.gtag) {
    window.gtag('event', 'search', {
      search_term: 'omitted',
    });
  }
};

/**
 * Track conversion events
 */
export const trackConversion = (conversionType, value = null) => {
  const eventData = {
    event: `conversion_${conversionType}`,
    value: value,
    page: coarsePath(window.location.pathname),
    timestamp: new Date().toISOString()
  };

  if (window.gtag) {
    window.gtag('event', `conversion_${conversionType}`, {
      value: value,
      currency: 'USD'
    });
  }

  if (process.env.NODE_ENV === 'development') {
    console.log('Conversion Tracked:', eventData);
  }
};

/**
 * Track scroll depth (engagement metric)
 */
export const trackScrollDepth = () => {
  let maxScroll = 0;
  const thresholds = [25, 50, 75, 90, 100];

  const handleScroll = () => {
    const scrollPercent = Math.round(
      ((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight) * 100
    );

    if (scrollPercent > maxScroll) {
      maxScroll = scrollPercent;
      
      thresholds.forEach(threshold => {
        if (scrollPercent >= threshold && maxScroll < threshold + 5) {
          if (window.gtag) {
            window.gtag('event', 'scroll', {
              scroll_depth: threshold
            });
          }
        }
      });
    }
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
};

/**
 * Track time on page
 */
export const trackTimeOnPage = () => {
  const startTime = Date.now();
  
  const trackExit = () => {
    const timeOnPage = Math.round((Date.now() - startTime) / 1000);
    
    if (window.gtag) {
      window.gtag('event', 'time_on_page', {
        value: timeOnPage
      });
    }
  };

  window.addEventListener('beforeunload', trackExit);
  return () => window.removeEventListener('beforeunload', trackExit);
};
